import type { ApiResult, DeviceBootstrapSource } from './api.js';

export interface DeviceCredentialSourceOptions {
  /**
   * Issues credentials through B1 `POST /api/v1/devices/{id}/credentials` and returns the `Device`
   * (the `data` of the envelope). Every call ROTATES the secret and resets B1's registration proof.
   */
  readonly issue: (deviceId: string, signal?: AbortSignal) => Promise<ApiResult<unknown>>;
  readonly now?: () => number;
  /** Never rotate more often than this. A REGISTER 401 just after rotation usually means the PBX has not loaded the new digest yet. */
  readonly minRotationIntervalMs?: number;
  /** Within the rotation window, reuse cached credentials only while they have at least this long left. */
  readonly reuseRemainingMs?: number;
}

export const MIN_CREDENTIAL_ROTATION_MS = 60_000;

/**
 * Canonical `DeviceBootstrapSource`. Credentials live only in this object's memory and are
 * returned as the raw canonical `Device`; the engine validates them. Concurrent loads share one
 * request, and rotation is rate-limited so retries cannot churn the PBX digest.
 */
export class DeviceCredentialSource implements DeviceBootstrapSource {
  private cached: { deviceId: string; device: unknown; expiresAt: number; receivedAt: number } | null = null;
  private inflight: Promise<ApiResult<unknown>> | null = null;
  private readonly now: () => number;
  private readonly minRotation: number;
  private readonly reuseRemaining: number;

  constructor(private readonly options: DeviceCredentialSourceOptions) {
    this.now = options.now ?? Date.now;
    this.minRotation = options.minRotationIntervalMs ?? MIN_CREDENTIAL_ROTATION_MS;
    this.reuseRemaining = options.reuseRemainingMs ?? 120_000;
  }

  /** Seed with the `Device` from `POST /api/v1/devices` so the first load does not rotate it. */
  seed(device: unknown): void { this.remember(device); }

  forget(): void { this.cached = null; }

  load(deviceId: string): Promise<ApiResult<unknown>> {
    const id = deviceId.toLowerCase();
    const now = this.now();
    const c = this.cached;
    if (c && c.deviceId === id && now - c.receivedAt < this.minRotation && c.expiresAt - now > this.reuseRemaining) {
      return Promise.resolve({ ok: true, value: c.device });
    }
    this.inflight ??= this.rotate(id).finally(() => { this.inflight = null; });
    return this.inflight;
  }

  private async rotate(id: string): Promise<ApiResult<unknown>> {
    const result = await this.options.issue(id);
    if (result.ok) {
      this.cached = null;
      this.remember(result.value);
    } else if (result.kind === 'http' && result.status === 404) {
      this.cached = null;
    }
    return result;
  }

  private remember(device: unknown): void {
    if (typeof device !== 'object' || device === null) return;
    const d = device as { id?: unknown; bootstrap?: { status?: unknown; expires_at?: unknown } };
    if (typeof d.id !== 'string' || d.bootstrap?.status !== 'issued' || typeof d.bootstrap.expires_at !== 'string') return;
    const expiresAt = Date.parse(d.bootstrap.expires_at);
    if (!Number.isFinite(expiresAt)) return;
    this.cached = { deviceId: d.id.toLowerCase(), device, expiresAt, receivedAt: this.now() };
  }
}
